// frontend/src/services/api.js
import axios from 'axios';

/** Auto-detect base URL (CRA). */
function detectBase() {
  // Local dev (CRA runs on :3000)
  if (typeof window !== 'undefined' &&
    /^(localhost|127\.0\.0\.1):3000$/.test(window.location.host)) {
    return 'http://127.0.0.1:5050';
  }
  // Prod: same-origin '/api' (Nginx should proxy /api -> Flask :5050)
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/api`;
  }
  // Fallback to CRA env or localhost
  return process.env.REACT_APP_API_BASE || 'http://127.0.0.1:5050';
}

// ✅ Export this so other files can import it
export const API_BASE_URL = detectBase(); // Flask back end URL

// ── Axios interceptors for JWT auth ─────────────────────────────────

// Request interceptor: attach Bearer token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('ayni-access-token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: handle 401 → redirect to /login, 403 → upgrade modal
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isAuthEndpoint = url.includes('/auth/');
      const isDigestEndpoint = url.includes('/digest/');
      if (!isAuthEndpoint && !isDigestEndpoint) {
        localStorage.removeItem('ayni-access-token');
        localStorage.removeItem('ayni-refresh-token');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    // Tier upgrade required — only dispatch for user-initiated actions (POST/PUT/DELETE), not background GET fetches
    if (error.response?.status === 403 &&
        (error.response?.data?.error === 'upgrade_required' || error.response?.data?.error === 'property_limit_reached') &&
        error.config?.method !== 'get') {
      window.dispatchEvent(new CustomEvent('ayni-upgrade-required', {
        detail: error.response.data,
      }));
    }
    return Promise.reject(error);
  }
);

/** Resolve shared key for protected /run_calendar.
 *  Priority: <meta name="x-ayni-key" content="..."> (runtime) → REACT_APP_AYNI_RUN_KEY (build-time)
 */
function getAyniRunKey() {
  if (typeof document !== 'undefined') {
    const m = document.querySelector('meta[name="x-ayni-key"]');
    const fromMeta = m?.getAttribute('content')?.trim();
    if (fromMeta) return fromMeta;
  }
  return (process.env.REACT_APP_AYNI_RUN_KEY || '').trim();
}

// Keep a memoized read so we don’t query DOM repeatedly
const AYNI_RUN_CALENDAR_KEY = getAyniRunKey();

// Fetch all properties
export const getProperties = async () => {
  try {
    console.log("DEBUG: Making API call to /properties...");
    const response = await axios.get(`${API_BASE_URL}/properties`);
    console.log("DEBUG: API response:", response);
    return response.data; // Ensure the full response is returned
  } catch (err) {
    console.error("DEBUG: Error in getProperties API call:", err);
    throw err;
  }
};

// Fetch all properties with dashboard-specific data (reservation + cleaner info)
export const getPropertiesDashboard = async () => {
  try {
    console.log("DEBUG: Making API call to /properties/dashboard...");
    const response = await axios.get(`${API_BASE_URL}/properties/dashboard`);
    console.log("DEBUG: Dashboard API response:", response);
    return response.data;
  } catch (err) {
    console.error("DEBUG: Error in getPropertiesDashboard API call:", err);
    throw err;
  }
};

// Fetch a single property by ID
export const getProperty = async (propertyId) => {
  const response = await axios.get(`${API_BASE_URL}/properties/${propertyId}`);
  return response.data;
};

// Add a new property
export const addProperty = async (propertyData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/properties`, propertyData);
    return response.data;
  } catch (error) {
    console.error("Error adding property:", error);
    throw error;
  }
};

// Update a property by ID
export const updateProperty = async (propertyId, updatedData) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/properties/${propertyId}`, updatedData);
    return response.data;
  } catch (error) {
    console.error(`Error updating property ID ${propertyId}:`, error);
    throw error;
  }
};

// Delete a property by ID
export const deleteProperty = async (propertyId) => {
  try {
    console.log(`DEBUG: Sending DELETE request for property ID: ${propertyId}`);
    const response = await axios.delete(`${API_BASE_URL}/properties/${propertyId}`);
    console.log("DEBUG: Property deleted successfully:", response);
    return response.data;
  } catch (error) {
    console.error(`Error deleting property ID ${propertyId}:`, error.response || error.message);
    throw error;
  }
};

// Fetch all cleaners
export const getCleaners = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/cleaners`);
    return response.data;
  } catch (error) {
    console.error("Error fetching cleaners:", error);
    throw error;
  }
};

// Add a new cleaner
export const addCleaner = async (cleanerData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/cleaners`, cleanerData);
    return response.data;
  } catch (error) {
    console.error("Error adding cleaner:", error);
    throw error;
  }
};

// Update a cleaner by ID
export const updateCleaner = async (cleanerId, updatedData) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/cleaners/${cleanerId}`, updatedData);
    return response.data;
  } catch (error) {
    console.error(`Error updating cleaner ID ${cleanerId}:`, error);
    throw error;
  }
};

// Delete a cleaner by ID
export const deleteCleaner = async (cleanerId) => {
  try {
    console.log(`DEBUG: Sending DELETE request for cleaner ID: ${cleanerId}`);
    const response = await axios.delete(`${API_BASE_URL}/cleaners/${cleanerId}`);
    console.log("DEBUG: Cleaner deleted successfully:", response);
    return response.data;
  } catch (error) {
    console.error(`Error deleting cleaner ID ${cleanerId}:`, error.response || error.message);
    throw error;
  }
};

// Assign a cleaner to a property
export const assignCleaner = async (assignmentData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/assign_cleaner`, assignmentData);
    return response.data;
  } catch (error) {
    console.error("Error assigning cleaner:", error);
    throw error;
  }
};

// Cleaner responsiveness stats
export const getCleanerStats = async () => {
  const response = await axios.get(`${API_BASE_URL}/cleaner-stats`);
  return response.data;
};

// Cleaner stats with per-property breakdown
export const getCleanerStatsDetailed = async () => {
  const response = await axios.get(`${API_BASE_URL}/cleaner-stats/detailed`);
  return response.data;
};

// Verification pass rate analytics
export const getVerificationStats = async () => {
  const response = await axios.get(`${API_BASE_URL}/verification-stats`);
  return response.data;
};

// Smart lock status (Seam)
export const getLockStatus = async () => {
  const response = await axios.get(`${API_BASE_URL}/locks/status`);
  return response.data;
};

export const getBatteryStats = async () => {
  const response = await axios.get(`${API_BASE_URL}/battery-stats`);
  return response.data;
};

export const getBatteryHistory = async (days = 30) => {
  const response = await axios.get(`${API_BASE_URL}/battery-history?days=${days}`);
  return response.data;
};

// Portfolio analytics (occupancy, cleanings, scores, trends, top cleaners)
export const getPortfolioAnalytics = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.property_id) query.set('property_id', params.property_id);
  if (params.days !== undefined) query.set('days', params.days);
  const qs = query.toString();
  const response = await axios.get(`${API_BASE_URL}/portfolio-analytics${qs ? `?${qs}` : ''}`);
  return response.data;
};

// Fetch property status
export const getPropertyStatus = async (propertyId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/properties/${propertyId}/status`);
    return response.data.status; // "Vacant" | "Occupied"
  } catch (err) {
    console.error(`Error fetching status for property ${propertyId}:`, err);
    return "Unknown";
  }
};

/** 🔁 Run calendar script for a property by ID.
 *  - Sends X-AYNI-KEY from meta/env when present.
 *  - If missing, backend will 401; we surface a friendly warning in dev console.
 */
export const runCalendarScript = async (propertyId) => {
  try {
    const headers = {};
    if (AYNI_RUN_CALENDAR_KEY) {
      headers['X-AYNI-KEY'] = AYNI_RUN_CALENDAR_KEY;
    } else {
      // eslint-disable-next-line no-console
      console.warn(
        '[runCalendarScript] No X-AYNI-KEY configured. ' +
        'Set REACT_APP_AYNI_RUN_KEY in your frontend env or add ' +
        '<meta name="x-ayni-key" content="..."> to index.html.'
      );
    }

    const response = await axios.post(
      `${API_BASE_URL}/properties/${propertyId}/run_calendar`,
      null,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error(`Error running calendar script for property ID ${propertyId}:`, error);
    throw error;
  }
};

// This will allow your frontend to call the /assignments endpoint.
export const getAssignments = () => {
  return axios.get(`${API_BASE_URL}/assignments`);
};

// Update Assignments End Point
export const updateAssignment = (assignmentId, updatedData) => {
  return axios.put(`${API_BASE_URL}/assignments/${assignmentId}`, updatedData);
};

// Delete Assignments End Point
export const deleteAssignment = (assignmentId) => {
  return axios.delete(`${API_BASE_URL}/assignments/${assignmentId}`);
};

// -------- Admin API calls (kept exactly as your baseline: relative to FE origin '/api/...') --------
export const getAdmins = async () => {
  const response = await axios.get('/api/admins');
  return response.data;
};

export const getAdminAssignments = async () => {
  const response = await axios.get('/api/admin-assignments');
  return response.data;
};

export const assignAdmin = async (data) => {
  return await axios.post('/api/admin-assignments', data);
};

export const updateAdminAssignment = async (id, data) => {
  return await axios.put(`/api/admin-assignments/${id}`, data);
};

export const deleteAdminAssignment = async (id) => {
  return await axios.delete(`/api/admin-assignments/${id}`);
};

export const addAdmin = async (admin) => {
  return await axios.post('/api/admins', admin);
};

export const updateAdmin = async (id, admin) => {
  return await axios.put(`/api/admins/${id}`, admin);
};

export const deleteAdmin = async (id) => {
  return await axios.delete(`/api/admins/${id}`);
};

// -------- Property Image API calls --------

// Upload an image for a property
export const uploadPropertyImage = async (propertyId, imageFile) => {
  const formData = new FormData();
  formData.append('image', imageFile);

  const uploadUrl = `${API_BASE_URL}/properties/${propertyId}/image`;
  console.log(`DEBUG: Uploading image to ${uploadUrl}`);
  console.log(`DEBUG: File name: ${imageFile.name}, size: ${imageFile.size}, type: ${imageFile.type}`);

  try {
    const response = await axios.post(
      uploadUrl,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    console.log('DEBUG: Upload successful:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error uploading image for property ID ${propertyId}:`, error);
    if (error.response) {
      console.error('DEBUG: Server response status:', error.response.status);
      console.error('DEBUG: Server response data:', error.response.data);
    } else if (error.request) {
      console.error('DEBUG: No response received. Request:', error.request);
    }
    throw error;
  }
};

// Delete an image for a property
export const deletePropertyImage = async (propertyId) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/properties/${propertyId}/image`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting image for property ID ${propertyId}:`, error);
    throw error;
  }
};

// Get full image URL (handles relative paths)
export const getPropertyImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  // If it's a relative path, prepend the API base URL
  if (imageUrl.startsWith('/')) {
    return `${API_BASE_URL}${imageUrl}`;
  }
  return imageUrl;
};

// -------- Verification API calls --------

export const getVerifications = async (propertyId) => {
  const response = await axios.get(`${API_BASE_URL}/verifications/${propertyId}`);
  return response.data;
};

export const getVerification = async (propertyId, reservationUid) => {
  const response = await axios.get(`${API_BASE_URL}/verification/${propertyId}/${encodeURIComponent(reservationUid)}`);
  return response.data;
};

export const rescoreVerification = async (verificationId) => {
  const response = await axios.post(`${API_BASE_URL}/verification/${verificationId}/rescore`);
  return response.data;
};

export const getVerificationPhotoUrl = (filePath) => {
  if (!filePath) return null;
  return `${API_BASE_URL}/uploads/${filePath}`;
};

// Get space verifications for a specific verification
export const getSpaceVerifications = async (propertyId, reservationUid) => {
  const response = await axios.get(`${API_BASE_URL}/verification/${propertyId}/${encodeURIComponent(reservationUid)}/spaces`);
  return response.data;
};

// Admin override a space verification (approve/reject with optional comment)
export const overrideSpaceVerification = async (spaceVerificationId, decision, comment = null) => {
  const payload = { decision };
  if (comment) payload.comment = comment;
  const response = await axios.post(`${API_BASE_URL}/space-verification/${spaceVerificationId}/override`, payload);
  return response.data;
};

// Reference Photos
export const getReferencePhotos = async (propertyId) => {
  const response = await axios.get(`${API_BASE_URL}/properties/${propertyId}/reference-photos`);
  return response.data;
};

export const uploadReferencePhoto = async (propertyId, spaceName, imageFile) => {
  const formData = new FormData();
  formData.append('space_name', spaceName);
  formData.append('image', imageFile);
  const response = await axios.post(`${API_BASE_URL}/properties/${propertyId}/reference-photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const deleteReferencePhoto = async (photoId) => {
  const response = await axios.delete(`${API_BASE_URL}/reference-photo/${photoId}`);
  return response.data;
};

export const getReferencePhotoUrl = (filePath) => {
  if (!filePath) return null;
  return `${API_BASE_URL}/uploads/${filePath}`;
};

// -------- Digest Dashboard (token-authenticated) --------

export const fetchDigestData = (token) => {
  return axios.get(`${API_BASE_URL}/digest/data`, { params: { token } });
};

export const digestDispatch = (token, data) => {
  return axios.post(`${API_BASE_URL}/digest/dispatch?token=${encodeURIComponent(token)}`, data);
};

export const refreshDigestToken = (token) => {
  return axios.get(`${API_BASE_URL}/digest/refresh-token`, { params: { token } });
};

// -------- Auth API calls --------

export const authLogin = async (email, password) => {
  const response = await axios.post(`${API_BASE_URL}/auth/login`, { email, password });
  return response.data;
};

export const authRegister = async (name, email, password) => {
  const response = await axios.post(`${API_BASE_URL}/auth/register`, { name, email, password });
  return response.data;
};

export const authGoogleLogin = async (id_token) => {
  const response = await axios.post(`${API_BASE_URL}/auth/google-login`, { id_token });
  return response.data;
};

export const authRefreshToken = async (refresh_token) => {
  const response = await axios.post(`${API_BASE_URL}/auth/refresh`, { refresh_token });
  return response.data;
};

export const authGetMe = async () => {
  const response = await axios.get(`${API_BASE_URL}/auth/me`);
  return response.data;
};

export const authUpdateProfile = async (data) => {
  const response = await axios.put(`${API_BASE_URL}/auth/me`, data);
  return response.data;
};

export const authLogout = async () => {
  const response = await axios.post(`${API_BASE_URL}/auth/logout`);
  return response.data;
};

// -------- Allowed Emails (superadmin) --------

export const getAllowedEmails = async () => {
  const response = await axios.get(`${API_BASE_URL}/auth/allowed-emails`);
  return response.data;
};

export const addAllowedEmail = async (email) => {
  const response = await axios.post(`${API_BASE_URL}/auth/allowed-emails`, { email });
  return response.data;
};

export const deleteAllowedEmail = async (id) => {
  const response = await axios.delete(`${API_BASE_URL}/auth/allowed-emails/${id}`);
  return response.data;
};

// User management (superadmin)
export const listUsers = async () => {
  const response = await axios.get(`${API_BASE_URL}/auth/users`);
  return response.data;
};

export const updateUserTier = async (userId, tier) => {
  const response = await axios.put(`${API_BASE_URL}/auth/users/${userId}/tier`, { tier });
  return response.data;
};

export const getUserDetail = async (userId) => {
  const response = await axios.get(`${API_BASE_URL}/auth/users/${userId}`);
  return response.data;
};

export const impersonateUser = async (userId) => {
  const response = await axios.post(`${API_BASE_URL}/auth/users/${userId}/impersonate`);
  return response.data;
};

// -------- Chat --------
export const sendChatMessage = async (message, history = []) => {
  const response = await axios.post(`${API_BASE_URL}/chat`, { message, history }, {
    timeout: 60000, // 60s — tool-use loops can take 15-30s
  });
  return response.data;
};